import { ToastId } from "@chakra-ui/toast";
import log from "loglevel";
import { BaseSyntheticEvent, useCallback, useRef } from "react";
import { FieldErrors, FieldValues, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormSubmitEvent } from "shared/components/form/FormSubmitEvent";
import { FormOnErrorFn } from "shared/components/form/FormTypes";
import { useToast } from "shared/components/toast/useToast";
import {
  ApiErrorData,
  filterExcludedNonFieldErrors,
  FlatFormFieldError,
  flattenApiErrorData,
  flattenFormFieldErrors,
  getApiErrorFromResponse,
  getErrorMessage,
  isFieldPathValidFormInput,
  setFormFieldErrors,
  useShowInternalErrorDetails,
} from "shared/utils/ErrorUtils";

interface UseFormSubmitProps<T extends FieldValues, R> {
  form: UseFormReturn<T>;
  onSubmit: (values: T) => R | Promise<R>;
  onSuccess?: (data: R, values: T) => void | Promise<void>;
  onSaveProgress?: (values: T) => R | Promise<R>;
  onError?: FormOnErrorFn<T>;
  onBeforeSubmit?: () => void;
  successMessage?: string;
  excludeNonFieldErrors?: (keyof FieldErrors<T>)[];
}

export const useFormSubmitWrapper = <T extends FieldValues, R>({
  form,
  successMessage,
  onSubmit,
  onSuccess,
  onSaveProgress,
  onError,
  onBeforeSubmit,
  excludeNonFieldErrors,
}: UseFormSubmitProps<T, R>) => {
  const { t } = useTranslation(["common"]);
  const { showToast, closeToast } = useToast();

  const toastIdRef = useRef<ToastId>();
  const showInternalErrors = useShowInternalErrorDetails();
  // Only show toast if no errors are shown in the form.
  // This should always be considered a bug, but we want to
  // indicate to the user that something went wrong.
  const checkNonFieldErrors = useCallback(
    (
      errors: FlatFormFieldError[],
      fallbackMessage = t("common:error.invalidInput"),
    ) => {
      const nonFieldErrors = errors.filter(
        (e) => !isFieldPathValidFormInput(form, e.fieldPath),
      );
      const hasNonFieldErrors = nonFieldErrors.length > 0;
      if (hasNonFieldErrors) {
        log.warn("Errors for missing fields", nonFieldErrors);
      }
      const shouldShowFormErrorToast =
        hasNonFieldErrors && nonFieldErrors.length === errors.length;
      if (shouldShowFormErrorToast) {
        const message = showInternalErrors
          ? [
              fallbackMessage,
              "",
              `[${t("common:internalErrorDetails")}]`,
              nonFieldErrors.map((e) =>
                [e.fieldPath, e.error].filter(Boolean).join(": "),
              ),
            ]
              .flat()
              .join("\n")
          : fallbackMessage;
        toastIdRef.current = showToast({
          variant: "error",
          message,
        });
      }
    },
    [form, showInternalErrors, showToast, t],
  );

  return useCallback(
    (event: BaseSyntheticEvent) => {
      // stopPropagation is needed to prevent any parent form from submitting.
      // see: https://github.com/orgs/react-hook-form/discussions/3704#discussioncomment-4152480
      event.stopPropagation();
      onBeforeSubmit?.();
      return form.handleSubmit(
        async (_, arg1) => {
          const formSubmitEvent = arg1?.nativeEvent as FormSubmitEvent;
          const formData = form.getValues();
          try {
            if (toastIdRef.current) {
              closeToast(toastIdRef.current);
            }
            const data =
              formSubmitEvent.detail?.skipOnSuccess && onSaveProgress
                ? await onSaveProgress(formData)
                : await onSubmit(formData);

            // Clear isDirty flag without changing field values
            form.reset(formData, { keepValues: true });

            // trigger onSuccess using setTimeout to run after form state updates
            // https://codingshower.com/react-execute-code-immediately-after-set-state-update-and-re-render/
            setTimeout(async () => {
              if (!formSubmitEvent.detail?.skipOnSuccess) {
                await onSuccess?.(data, formData);
              }
              if (successMessage) {
                toastIdRef.current = showToast({
                  variant: "success",
                  message: successMessage,
                });
              }
              // NOTE: Timeout value of 0 does not work in Safari.
            }, 100);
          } catch (e) {
            const isErrorHandled = await onError?.(e, formData, {
              form,
            });
            if (isErrorHandled) {
              return;
            }
            const apiError = getApiErrorFromResponse(e);
            if (apiError?.code === "invalid" && apiError.data) {
              const flatErrors = flattenApiErrorData(
                filterExcludedNonFieldErrors(
                  apiError.data,
                  excludeNonFieldErrors,
                ) as ApiErrorData,
              );
              setFormFieldErrors(form, flatErrors);
              checkNonFieldErrors(flatErrors, apiError.message);
            } else {
              toastIdRef.current = showToast({
                variant: "error",
                message: getErrorMessage(t, e),
              });
            }
          }
        },
        (errors) => {
          log.warn("Form validation error", errors);
          const flatErrors = flattenFormFieldErrors(
            filterExcludedNonFieldErrors(
              errors,
              excludeNonFieldErrors,
            ) as FieldErrors<T>,
          );
          checkNonFieldErrors(flatErrors);
        },
      )(event);
    },
    [
      checkNonFieldErrors,
      closeToast,
      excludeNonFieldErrors,
      form,
      onBeforeSubmit,
      onError,
      onSubmit,
      onSuccess,
      onSaveProgress,
      showToast,
      successMessage,
      t,
    ],
  );
};
